import React, { useState, useEffect } from 'react';

/**
 * `RenewalLandingPage` is the entry page for the renewals journey.
 *
 * Responsibilities:
 * - Handles VRN/VIN + password login to retrieve renewal details.
 * - Tracks login attempts and lockout messaging via `SiteApi`.
 * - Supports call-centre record-id based retrieval and validates that VRN
 *   matches the returned vehicle.
 * - Populates state needed for later renewals pages (customer, vehicle,
 *   dealer code, token, policy start date, campaign/promo flags).
 * - Routes to late-renewal acceptance when applicable.
 * - Renders CMS-driven landing content including "Why" section, media, and
 *   "Any questions" contact panel.
 */
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import NavBar from '../../../common/NavBar';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faStopwatch } from '@fortawesome/free-solid-svg-icons/faStopwatch';
import { RenewalsApi } from '../../../../api/RenewalsApi';
import {
  RenewalCredentials,
  RenewalDetails,
  PersonalDetailType,
  VehicleState
} from '../../../common/CommonTypes';
import ReactPlayer from 'react-player';
import {
  Journey,
  PageAbsolutePath,
  VehicleSource
} from '../../../../consts/Common';
import './LandingPage.scss';
import WhySection from './WhySection';
import PageHeader from '../../../common/PageHeader';
import Login, { LoginErrors } from '../../../common/Login/Login';
import AnyQuestionsSection from './AnyQuestionsSection';
import { CommonValidation } from '../../../common/CommonValidation/commonValidation';
import { SelectInputHelper } from '../../../common/SelectInput/SelectInput';
import LoadingContainer from '../../../common/LoadingContainer/LoadingContainer';
import { useTranslation } from 'react-i18next';
import { setCountryCode } from '../../../common/HelperMethods';
import { blankVehicle } from '../../../testData/journeyHelper';
import { useAppDispatch, useAppSelector } from '../../../../app-state/Hooks';
import {
  setDealerCode as setDealerCodeAction,
  setLoggedIn,
  setOriginalSupplyingDealer as setOriginalSupplyingDealerAction
} from '../../../../app-state/slices/UserSlice';
import { setCustomer } from '../../../../app-state/slices/CustomerSlice';

type RenewalLandingPageProps = {
  callCenterVrn?: string;
  expAuthId?: string;
  campaignId?: string;
};

const initialRenewalsJourneyState = {
  personalDetail: {
    title: SelectInputHelper.NullValue(),
    firstName: '',
    surname: '',
    dateOfBirth: undefined,
    email: '',
    confirmEmail: '',
    phone: '',
    addressDetails: {
      addressLine1: '',
      addressLine2: '',
      townOrCity: '',
      region: '',
      country: '',
      postCode: ''
    }
  },
  vehicle: {
    vehicleDetails: blankVehicle,
    currentMileage: null,
    vehiclePurchasePrice: 0,
    vehicleDeliveryDate: '',
    salesId: '',
    dealerReference: '',
    deliveryDateEstimated: false,
    vehicleSource: VehicleSource.System
  },
  dealerCode: '',
  token: '',
  tokenExpiresOn: new Date(),
  policyStartDate: undefined as Date | undefined,
  promoCode: '',
  campaignId: 0
};

const RenewalLandingPage = ({
  callCenterVrn,
  expAuthId,
  campaignId
}: RenewalLandingPageProps): JSX.Element => {
  const [errors, setErrors] = useState<LoginErrors>({});
  const [loading, setLoading] = useState(false);
  const [isValid, setIsValid] = useState(false);
  const [failedAttempts, setFailedAttempts] = useState(0);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const site = useAppSelector((s) => s); // TODO: consider render optimization
  const appConfig = useAppSelector((s) => s.appConfig);
  const cms = site.content.landingPage;
  const hideLateRenewalPage = site.content.hideLateRenewalPage;

  const [dealerCode, setDealerCode] = useState(
    initialRenewalsJourneyState.dealerCode
  );

  const [policyStartDate, setPolicyStartDate] = useState<Date | undefined>(
    initialRenewalsJourneyState.policyStartDate
  );

  const [token, setToken] = useState(initialRenewalsJourneyState.token);
  const [tokenExpiresOn, setTokenExpiresOn] = useState(
    initialRenewalsJourneyState.tokenExpiresOn
  );

  const [personalDetail, setPersonalDetails] = useState<PersonalDetailType>(
    initialRenewalsJourneyState.personalDetail
  );
  const [vehicle, setVehicle] = useState<VehicleState>(
    initialRenewalsJourneyState.vehicle
  );

  const [isLateRenewal, setIsLateRenewal] = useState(false);

  const [promoCode, setPromoCode] = useState(
    initialRenewalsJourneyState.promoCode
  );

  const [renewalCampaignId, setRenewalCampaignId] = useState(
    initialRenewalsJourneyState.campaignId
  );

  const { t } = useTranslation();

  const recordId = appConfig.recordId ?? '';

  console.log('countryCode-' + appConfig.countryCode);

  useEffect(() => {
    const renewalSourceSystem = appConfig.renewalSourceSystem;
    if (
      CommonValidation.isNotEmpty(recordId) &&
      CommonValidation.isNotEmpty(callCenterVrn)
    ) {
      const getRenewals = async () => {
        const controller = new AbortController();

        const successCallback = (data: RenewalDetails) => {
          const vrn = data.vehicleDetail?.vrn;
          if (vrn && callCenterVrn?.toUpperCase() === vrn.toUpperCase()) {
            data.vehicleDetail.errorOrigin = 'vrn';
            data.vehicleDetail.errorMessage = t(
              'common-error-msg.api-error-msg.renewal-recordid-error'
            );
            setDetails(data);
            setFailedAttempts(data.failedAttempts || 0);
            setLoading(false);
          } else {
            const path = setCountryCode(
              PageAbsolutePath.Error,
              appConfig.countryCode
            );
            navigate(path, {
              state: {
                error: t('landing-page.vehicle-registration-not-match-error')
              }
            });
          }
        };

        const failureCallback = () => {
          const path = setCountryCode(
            PageAbsolutePath.Error,
            appConfig.countryCode
          );
          navigate(path, {
            state: {
              error: t('landing-page.record-not-found-error')
            }
          });

          setIsValid(false);
          setLoading(false);
        };

        setLoading(true);

        new RenewalsApi().getRenewalDetailsFromRecordId(
          {
            recordId,
            vrn: callCenterVrn || '',
            renewalSourceSystem: renewalSourceSystem || '',
            expAuthId: expAuthId || '',
            campaignId: campaignId || '0',
            brandName: site.brand.brandName,
            culture: appConfig.cultureCode,
            lateRenewalAllowedDays: site.content.lateRenewalAllowedDays
          },
          site.user.token,
          successCallback,
          failureCallback,
          controller.signal
        );

        return () => {
          controller.abort();
        };
      };

      getRenewals();
    }
  }, [recordId, callCenterVrn]);

  const setLoginErrors = (isLockedOut: boolean, attempts: number) => {
    const newError = {
      vrnError: '',
      passwordError: '',
      invalidCredentialsError: ''
    };

    const oneAttemptLeft = attempts === site.appConfig.maxLoginAttempts - 1;

    if (isLockedOut) {
      newError.invalidCredentialsError = CommonValidation.isNotEmpty(
        cms.multipleLoginAttemptsMessage
      )
        ? cms.multipleLoginAttemptsMessage
        : t('landing-page.login-attempts-exceeded');
    } else if (oneAttemptLeft) {
      newError.invalidCredentialsError = t(
        'landing-page.customer-invalid-credential-one-attempt-left'
      );
    } else {
      newError.invalidCredentialsError = t(
        'landing-page.customer-invalid-credential'
      );
    }

    setErrors(newError);
    setFailedAttempts(attempts);
    setIsValid(false);
    setLoading(false);
  };

  const getRenewalDetails = (login: string, password: string) => {
    const controller = new AbortController();

    const successCallback = (data: RenewalDetails) => {
      if (data.isLockedOut || data.invalidCredentials) {
        setLoginErrors(data.isLockedOut, data.failedAttempts);
      } else {
        setDetails(data);
        setLoading(false);
      }
    };

    const failureCallback = () => {
      setLoginErrors(false, 0);
    };

    let vin = '';
    let vrn = '';
    const brandName = site.brand.brandName;
    const brandId = site.brand.brandId;
    const openSiteDealerCode = site.brand.openSiteDealerCode;
    const culture = appConfig.cultureCode;
    const lateRenewalAllowedDays = site.content.lateRenewalAllowedDays;

    if (cms.useVinInput) {
      vin = login;
    } else {
      vrn = login;
    }

    const credentials: RenewalCredentials = {
      vrn,
      vin,
      password,
      brandName,
      brandId,
      openSiteDealerCode,
      culture,
      lateRenewalAllowedDays
    };

    setLoading(true);

    new RenewalsApi().getRenewalDetails(
      credentials,
      site.user.token,
      successCallback,
      failureCallback,
      controller.signal
    );

    return () => {
      controller.abort();
    };
  };

  const setDetails = (data: RenewalDetails) => {
    setToken(data.token);
    setTokenExpiresOn(data.tokenExpiresOn);
    setDealerCode(data.dealerCode);
    setPersonalDetails(data.customerDetail);
    dispatch(setCustomer(data.customerDetail)); // holds initial customer data untouched
    setPolicyStartDate(data.policyStartDate);

    setPromoCode(data.promoCode);

    if (data.campaignId) {
      setRenewalCampaignId(data.campaignId);
    }

    if (data.dealerCode) {
      dispatch(setDealerCodeAction(data.dealerCode));
    }

    if (data.originalSupplyingDealer) {
      dispatch(setOriginalSupplyingDealerAction(data.originalSupplyingDealer));
    }

    const deliveryDate = JSON.stringify({
      value: data.vehicleDeliveryDate,
      utc: '0'
    });

    setVehicle({
      ...vehicle,
      vehicleDetails: data.vehicleDetail,
      vehicleDeliveryDate: deliveryDate,
      vehicleSource: data.vehicleSource
    });

    setIsLateRenewal(data.isLateRenewal);

    const isRenewalValid =
      !data.isRenewalPastExpiry || data.isRenewalExpiryApproved;

    setIsValid(isRenewalValid);

    if (!isRenewalValid) {
      setErrors({
        invalidCredentialsError: cms.renewalPeriodExpiredMessage
      });
      setLoading(false);
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const getPage = (): string => {
    const showLateRenewalPage = !hideLateRenewalPage && isLateRenewal === true;

    if (showLateRenewalPage) {
      return setCountryCode(
        PageAbsolutePath.Renewals.LateRenewalAcceptance,
        appConfig.countryCode
      );
    } else {
      return appConfig.journeyType === Journey.CallCentre
        ? setCountryCode(
            PageAbsolutePath.Renewals.VehicleDetails,
            appConfig.countryCode
          )
        : setCountryCode(
            PageAbsolutePath.Renewals.PersonalDetails,
            appConfig.countryCode
          );
    }
  };

  useEffect(() => {
    if (isValid === true) {
      dispatch(setLoggedIn(true));
      navigate(getPage(), {
        state: {
          personalDetail: personalDetail,
          vehicle: vehicle,
          dealerCode: dealerCode,
          token: token,
          tokenExpiresOn: tokenExpiresOn,
          policyStartDate: policyStartDate,
          isLateRenewal: isLateRenewal,
          promoCode: promoCode,
          campaignId: renewalCampaignId
        }
      });
    }
  }, [isValid]);

  const onClickLogin = (vreg: string, password: string) => {
    setErrors({});
    getRenewalDetails(vreg, password);
  };

  const showMedia = () => {
    if (CommonValidation.isNotEmpty(cms.videoUrl)) {
      return (
        <ReactPlayer
          data-testid='video-player'
          className='react-player'
          controls
          url={cms.videoUrl}
        />
      );
    } else if (CommonValidation.isNotEmpty(cms.wistiaEmbedUrl)) {
      return <div dangerouslySetInnerHTML={{ __html: cms.wistiaEmbedUrl }} />;
    } else {
      return (
        <img
          data-testid='alt-image'
          className='alt-image'
          src={cms.videoAlternativeImageUrl}
          alt={cms.videoAlternativeImageAltText}
        />
      );
    }
  };

  return (
    <>
      <Helmet>
        <title>{t('page-title.login')}</title>
      </Helmet>

      <div className='flex-column'>
        <LoadingContainer
          loading={appConfig.journeyType !== Journey.Renewals}
          message={t('loading-placeholder.loading-text')}>
          <NavBar />
          <div className='flex-row mobile-column landing-page--top-section display-block'>
            <div className='flex-column landing-page--intro-wrapper'>
              <div className='landing-page--intro m2-bottom'>
                <div className='page-header-line' />
                <PageHeader
                  title={cms.title}
                  isRenewalLandingPage={true}
                  description={cms.description}
                  information={cms.information}
                  warning={cms.warning}
                  descriptionClass='p1 extra-line-height lp-header-detail'
                />
                <div>
                  <Login
                    loginAttemptId={failedAttempts}
                    showEmail={false}
                    loginErrors={errors}
                    loading={loading}
                    onClickLogin={onClickLogin}
                    useVinInput={cms.useVinInput}
                  />
                  <p className='p4'>
                    <FontAwesomeIcon icon={faStopwatch} className='m05-right' />
                    {cms.journeyTimeNote}
                  </p>
                </div>
              </div>
            </div>
            <div className='flex-column'>
              <img
                className='landing-image'
                src={cms.backgroundImageUrl}
                alt={cms.backgroundImageAltText}
              />
            </div>
          </div>
          <WhySection cms={cms.whySection} showWhyTabs={true} />
          <div className='mobile-column'>
            <div className='product-video-panel'>
              <div className='product-video-details player-wrapper cms-renewal-video-section'>
                {showMedia()}
              </div>
            </div>
          </div>
          <div className='mobile-column'>
            <AnyQuestionsSection cms={cms.anyQuestionsSection} />
          </div>
        </LoadingContainer>
      </div>
    </>
  );
};

export default RenewalLandingPage;
